// Faz BP-polish (2026-05-13) — Sifre sifirlama sayfasi (admin paneli)
// Mail'deki linkle gelen kullanici: ?token=... ile bu sayfaya gelir.
// Yeni sifre + tekrar girer → POST /api/auth/reset-password → success → login.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Stack, Link } from '@mui/material';
import { resetPassword } from '../services/api';

const Logo = ({ size = 64 }) => (
  <Box
    aria-label="Miksan Plastik"
    sx={{
      width: size, height: size, borderRadius: '50%',
      bgcolor: '#FFFFFF',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 18px rgba(15,42,79,0.30)',
      mx: 'auto'
    }}
  >
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="55" textAnchor="middle" dominantBaseline="middle"
            fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fontSize="42" fill="#1E4FA0">
        MK
      </text>
    </svg>
  </Box>
);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) setError('Geçersiz veya eksik sıfırlama linki'); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Şifre en az 8 karakter olmalı'); return; }
    if (pw !== pw2) { setError('Şifreler eşleşmiyor'); return; }
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.error || 'Sıfırlama başarısız. Link süresi dolmuş olabilir.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0F2A4F 0%, #1E4FA0 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(15,42,79,0.18), 0 2px 8px rgba(15,42,79,0.10)',
        }}
        elevation={0}
      >
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Logo size={72} />
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
              Yeni Şifre Belirle
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En az 8 karakter, harf + rakam içermeli
            </Typography>
          </Box>
        </Stack>

        {done ? (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Şifreniz güncellendi. Giriş sayfasına yönlendiriliyorsunuz...
            </Alert>
          </Stack>
        ) : (
          <form onSubmit={submit}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            <TextField
              fullWidth label="Yeni Şifre" type="password" autoComplete="new-password" autoFocus
              value={pw} onChange={e => setPw(e.target.value)} sx={{ mb: 2 }} required disabled={!token}
            />
            <TextField
              fullWidth label="Yeni Şifre (tekrar)" type="password" autoComplete="new-password"
              value={pw2} onChange={e => setPw2(e.target.value)} sx={{ mb: 2.5 }} required disabled={!token}
            />
            <Button
              fullWidth variant="contained" type="submit" disabled={loading || !token} size="large"
              sx={{ py: 1.4, fontWeight: 700, textTransform: 'none', fontSize: 15, borderRadius: 2, mb: 1.5 }}
            >
              {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" underline="hover"
                    sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
                ← Giriş sayfasına dön
              </Link>
            </Box>
          </form>
        )}

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 3, fontSize: 11 }}>
          © {new Date().getFullYear()} Miksan Plastik · Tüm hakları saklıdır
        </Typography>
      </Card>
    </Box>
  );
}
